import React, { useState, useEffect } from "react";
import {
  TableRow,
  TableCell,
  Checkbox,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { toast } from "../../../utils/notifications";
import type { RFQItem } from "../../../types/rfq";
import type { TeamMember } from "../../../types";
import SavedRemarksDialog from './SavedRemarksDialog';
import CostPriceCell from './CostPriceCell';
import { ItemNameCell, NumberCell, QuotedPriceCell, RemarksCell } from './ItemRowCells';
import { applyMarkup, applyPrice } from "./itemPricing";
import { useItemMutations } from "./useItemMutations";
import { useItemRemarks } from "./useItemRemarks";
import { useItemValidation } from "./useItemValidation";

interface RFQItemRowProps {
  item: RFQItem;
  rfqId: number;
  canEdit: boolean;
  selectedUser: TeamMember | null;
  rfqStatus: string;
  isSelected?: boolean;
  onToggleSelect?: (itemId: number) => void;
}

const RFQItemRow: React.FC<RFQItemRowProps> = ({ item, rfqId, canEdit, selectedUser, rfqStatus, isSelected = false, onToggleSelect }) => {
  // Form state
  const [price, setPrice] = useState<string>(() => {
    if (item.quoted_price != null) {
      return Number(item.quoted_price).toFixed(2);
    }
    return "";
  });
  const [markup, setMarkup] = useState<string>(item.markup_percentage?.toString() || "");
  const [indentDays, setIndentDays] = useState<string>(item.indent_days?.toString() || "");
  const [itemName, setItemName] = useState(item.item_name);
  const [modelNumber, setModelNumber] = useState(item.model_number || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingModel, setIsEditingModel] = useState(false);

  // UI state
  const [isPriceModified, setIsPriceModified] = useState(false);
  const [isIndentModified, setIsIndentModified] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Update item mutation with optimistic updates
  const updateItemMutation = useItemMutations(rfqId, item.rfq_item_id, {
    onSaved: (variables) => {
      // Reset modification flags based on what was updated
      if ('price_validated' in variables || 'quoted_price' in variables) {
        setIsPriceModified(false);
      }

      if ('remark_tags' in variables || 'remarks' in variables) {
        setIsRemarksModified(false);
      }

      if ('item_name' in variables) {
        setIsEditingName(false);
      }

      if ('model_number' in variables) {
        setIsEditingModel(false);
      }

      if ('indent_days' in variables) {
        setIsIndentModified(false);
      }

      setIsUpdating(false);
    },
    onFailed: () => setIsUpdating(false),
  });

  const {
    isNonRefundable,
    isLimitedStock,
    customRemark,
    setIsRemarksModified,
    remarkPopover,
    savedRemarksOpen,
    handleCloseSavedRemarks,
    handleInsertSavedRemark,
    handleOpenRemarkPopover,
    handleRemarkToggle,
    getRemarksDisplay,
  } = useItemRemarks({
    item,
    save: (data) => updateItemMutation.mutate(data),
    setIsUpdating,
    setPrice,
    setMarkup,
    setIsPriceModified,
  });

  // Update local state when item changes. Price, markup and indent days are
  // dependencies too: a server-side change (apply previous quote, bulk "not
  // available", another user's edit) touches them without touching remarks,
  // and must still reach the inputs - unless the user is mid-edit. The
  // remarks half of this resync, on the same triggers, is in useItemRemarks.
  useEffect(() => {
    if (!isPriceModified && item.quoted_price?.toString() !== price) {
      // DON'T round price when it comes from the database
      // The price in the database is the validated/saved price and should be displayed as-is
      if (item.quoted_price !== null && item.quoted_price !== undefined) {
        setPrice(Number(item.quoted_price).toFixed(2));
      } else {
        if (item.price_validated) {
          console.warn(`[DATA WARNING] Item "${item.item_name}" (ID: ${item.rfq_item_id}) has price_validated=true but quoted_price is null`);
        }
        setPrice("");
      }
    }

    if (!isPriceModified && item.markup_percentage?.toString() !== markup) {
      setMarkup(item.markup_percentage?.toString() || "");
    }

    if (!isIndentModified && item.indent_days?.toString() !== indentDays) {
      setIndentDays(item.indent_days?.toString() || "");
    }
  }, [item.remark_tags, item.remarks, item.quoted_price, item.markup_percentage, item.indent_days]);

  // Auto-calculate markup if it's missing but cost_price and quoted_price are available
  useEffect(() => {
    if (
      !isPriceModified &&
      item.cost_price &&
      item.quoted_price &&
      item.cost_price > 0 &&
      !item.markup_percentage
    ) {
      const { markup: calculatedMarkup } = applyPrice(item.cost_price, item.quoted_price);
      if (calculatedMarkup != null) {
        setMarkup(calculatedMarkup);
      }
    }
  }, [item.cost_price, item.quoted_price, item.markup_percentage, isPriceModified]);

  const handleNameSave = () => {
    if (itemName !== item.item_name) {
      updateItemMutation.mutate({ item_name: itemName });
    } else {
      setIsEditingName(false);
    }
  };

  const handleModelSave = () => {
    if (modelNumber !== item.model_number) {
      updateItemMutation.mutate({ model_number: modelNumber });
    } else {
      setIsEditingModel(false);
    }
  };

  // Price change handler with markup calculation
  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPriceModified(true);
    const newPrice = e.target.value;
    setPrice(newPrice); // Don't round during manual entry

    // Calculate markup if we have a cost price and valid new price
    if (newPrice && !isNaN(parseFloat(newPrice))) {
      const { markup: newMarkup } = applyPrice(item.cost_price, parseFloat(newPrice));
      if (newMarkup != null) setMarkup(newMarkup);
    }
  };

  // Markup change handler with price calculation
  const handleMarkupChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsPriceModified(true);
    const newMarkup = e.target.value;
    setMarkup(newMarkup);

    // Calculate new price from cost price and markup (rounded to whole dollar)
    const newPrice = applyMarkup(item.cost_price, newMarkup);
    if (newPrice != null) setPrice(newPrice);
  };

  // Indent days change handler
  const handleIndentDaysChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsIndentModified(true);
    setIndentDays(e.target.value);
  };

  // Save pricing changes
  const handleSavePricing = () => {
    if (!isPriceModified) return;
    
    try {
      // Parse and validate the price
      const numericPrice = parseFloat(price);
      if (isNaN(numericPrice)) {
        throw new Error('Invalid price value');
      }
      
      // Use the exact price as entered - no rounding
      const finalPrice = numericPrice;
      
      // Update the display value
      setPrice(finalPrice.toFixed(2));
      
      // Ensure markup is a valid number if provided
      let markupValue: number | null = null;
      if (markup && markup.trim() !== '') {
        markupValue = parseFloat(parseFloat(markup).toFixed(1));
      }
      
      // Create update data
      const updateData = {
        quoted_price: finalPrice,
        markup_percentage: markupValue
      };
      
      setIsUpdating(true);
      updateItemMutation.mutate(updateData);
    } catch (error) {
      console.error('Error processing price update:', error);
      toast({
        title: "Error updating price",
        description: "Failed to update the price. Please try again.",
        severity: "error",
      });
    }
  };

  // Save indent days changes
  const handleSaveIndent = () => {
    if (!isIndentModified) return;
    
    try {
      // Ensure indent days is a valid integer
      const indentValue = parseInt(indentDays, 10);
      
      if (isNaN(indentValue)) {
        throw new Error('Invalid indent days value');
      }
      
      // Create update data
      const updateData = {
        indent_days: indentValue,
        // Don't reset validation flags - they should be controlled separately
      };
      
      setIsUpdating(true);
      updateItemMutation.mutate(updateData);
    } catch (error) {
      console.error('Error processing indent days update:', error);
      toast({
        title: "Error updating indent days",
        description: "Failed to update the indent days. Please try again.",
        severity: "error",
      });
    }
  };

  // Add new auto-save handlers for blur events
  const handlePriceBlur = () => {
    if (isPriceModified) {
      handleSavePricing();
    }
  };

  const handleMarkupBlur = () => {
    if (isPriceModified) {
      handleSavePricing();
    }
  };

  const handleIndentBlur = () => {
    if (isIndentModified) {
      handleSaveIndent();
    }
  };

  const { handleValidationClick, handleMarkItemNotAvailable } = useItemValidation({
    item,
    selectedUser,
    price,
    isPriceModified,
    setPrice,
    setIsPriceModified,
    setIsRemarksModified,
    save: (data) => updateItemMutation.mutate(data),
  });

  // Determine if fields should be disabled
  const isPriceDisabled = !canEdit || item.price_validated || isUpdating;
  const isRemarksDisabled = !canEdit || item.message_validated || isUpdating;

  const editableName = canEdit && (rfqStatus === 'DIY' || rfqStatus === 'MANUAL');

  return (
    <TableRow
      hover
      selected={isSelected}
      sx={{
        "&:nth-of-type(even):not(.Mui-selected)": { bgcolor: (t) => alpha(t.palette.primary.main, 0.018) },
        "& > td": { py: 1.25, verticalAlign: "middle" },
      }}
    >
      {/* Checkbox Column */}
      {canEdit && onToggleSelect && (
        <TableCell padding="checkbox">
          <Checkbox
            checked={isSelected}
            onChange={() => onToggleSelect(item.rfq_item_id)}
            size="small"
          />
        </TableCell>
      )}

      <ItemNameCell
        item={item}
        editable={editableName}
        itemName={itemName}
        setItemName={setItemName}
        isEditingName={isEditingName}
        startEditingName={() => setIsEditingName(true)}
        saveName={handleNameSave}
        modelNumber={modelNumber}
        setModelNumber={setModelNumber}
        isEditingModel={isEditingModel}
        startEditingModel={() => setIsEditingModel(true)}
        saveModel={handleModelSave}
      />
      <TableCell align="right" sx={{ fontWeight: 500 }}>{item.quantity_requested}</TableCell>
      <CostPriceCell item={item} itemName={itemName} modelNumber={modelNumber} />
      <QuotedPriceCell
        item={item}
        canEdit={canEdit}
        price={price}
        disabled={isPriceDisabled}
        onChange={handlePriceChange}
        onBlur={handlePriceBlur}
        validateDisabled={isPriceModified || updateItemMutation.isPending}
        notAvailableDisabled={updateItemMutation.isPending || !!item.price_validated}
        onValidate={() => handleValidationClick('price')}
        onMarkNotAvailable={handleMarkItemNotAvailable}
      />
      <NumberCell
        canEdit={canEdit}
        value={markup}
        onChange={handleMarkupChange}
        onBlur={handleMarkupBlur}
        disabled={isPriceDisabled}
        step={0.1}
        unit="%"
        width={84}
        validated={!!item.price_validated}
        readOnlyText={`${markup || "-"}%`}
      />
      <NumberCell
        canEdit={canEdit}
        value={indentDays}
        onChange={handleIndentDaysChange}
        onBlur={handleIndentBlur}
        step={1}
        unit="days"
        width={96}
        validated={false}
        readOnlyText={`${indentDays || "-"} days`}
      />
      <RemarksCell
        canEdit={canEdit}
        isNonRefundable={isNonRefundable}
        isLimitedStock={isLimitedStock}
        onToggle={handleRemarkToggle}
        customRemark={customRemark}
        onOpenRemarkPopover={handleOpenRemarkPopover}
        remarkPopover={remarkPopover}
        disabled={isRemarksDisabled}
        readOnlyText={canEdit ? "" : getRemarksDisplay()}
      />

      {/* Saved Remarks Dialog */}
      <SavedRemarksDialog
        open={savedRemarksOpen}
        onClose={handleCloseSavedRemarks}
        onInsertRemark={handleInsertSavedRemark}
      />
    </TableRow>
  );
};

export default RFQItemRow;
